/*
 * Occurra Input — mirrors the Figma design system Input component
 * (file kQs3kYj8njKAsjv55HP7N7, page 29:39).
 *
 * Every text field in the app renders through it, so the box chrome —
 * fill, border, radius, height, focus ring — has exactly one definition.
 *
 * Size (height, x-padding, slot gap, radius, type):
 *   - Mini    24px, px 8,  gap 4,  rounded-sm (4px), 12/16
 *   - Small   32px, px 10, gap 6,  rounded-md (6px), 14/20
 *   - Regular 36px, px 12, gap 8,  rounded-md (6px), 14/20
 *   - Large   40px, px 16, gap 12, rounded-lg (8px), 14/20
 *
 * State comes from the DOM rather than a prop — placeholder, :focus-within,
 * aria-invalid and disabled cover the seven Figma states:
 *   rest / value → border-occ-foreground-100 on bg-occ-background-input
 *   focus        → border-occ-brand + 3px ring-occ-brand-200
 *   error        → border-occ-red-1000, ring-occ-red-200 once focused
 *   disabled     → border-occ-foreground-50 at 50% opacity, `disabledReason` in a tooltip
 *
 * Slots mirror the Figma sub-layers: `leading`/`trailing` are the 20px
 * "Decoration" boxes holding a 16px glyph, `prepend`/`append` the muted
 * text either side of the value.
 */

import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { Hint } from './Hint';

const inputVariants = cva(
    [
        'flex w-full min-w-0 items-center border bg-occ-background-input',
        'text-occ-foreground-main transition-[color,box-shadow]',
        // The ring sits on the box so the decoration slots fall inside it. A text
        // field always matches :focus-visible when focused, so focus-within is exact.
        'focus-within:border-occ-brand focus-within:ring-[3px] focus-within:ring-occ-brand-200',
    ].join(' '),
    {
        variants: {
            size: {
                mini: 'h-6 gap-1 rounded-sm px-2 text-xs',
                small: 'h-8 gap-1.5 rounded-md px-2.5 text-sm',
                regular: 'h-9 gap-2 rounded-md px-3 text-sm',
                large: 'h-10 gap-3 rounded-lg px-4 text-sm',
            },
            invalid: {
                true: [
                    'border-occ-red-1000',
                    'focus-within:border-occ-red-1000 focus-within:ring-occ-red-200',
                ].join(' '),
                false: 'border-occ-foreground-100',
            },
            disabled: {
                true: 'border-occ-foreground-50 opacity-50',
                false: '',
            },
        },
        defaultVariants: { size: 'regular', invalid: false, disabled: false },
    },
);

type InputSize = 'mini' | 'small' | 'regular' | 'large';

/**
 * `size` is redeclared rather than inherited: the DOM's own `size` attribute is
 * a number (the field's character width), and this one names the design-system
 * height, so the native one has to be dropped for the union to take its place.
 */
interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'> {
    /** Classes for the chrome box around the field. */
    className?: string;
    /** Classes for the <input> element itself. */
    inputClassName?: string;
    size?: InputSize;
    /** Decorations outside the field's text run. */
    leading?: React.ReactNode;
    trailing?: React.ReactNode;
    /** Affixes inside the field's text run, beside the value. */
    prepend?: React.ReactNode;
    append?: React.ReactNode;
    /** Why the field is disabled, in a tooltip while it is. */
    disabledReason?: string;
}

/* Figma "Decoration left/right": a 20px box centring a 16px glyph. It grows
 * for a child that needs more room (a nested icon button) and stretches so a
 * full-height child — the number stepper's arrow column — can fill the box. */
function Decoration({ children }: { children: React.ReactNode }): React.JSX.Element {
    return (
        <span className={cn(
            'flex min-w-5 shrink-0 items-center justify-center self-stretch',
            "text-occ-foreground-secondary [&_svg:not([class*='size-'])]:size-4",
        )}
        >
            {children}
        </span>
    );
}

/* A caller clears a slot with null (SearchField's trailing) or leaves it out. */
function hasSlot(slot: React.ReactNode): boolean {
    return slot !== null && slot !== undefined;
}

function Affix({ children }: { children: React.ReactNode }): React.JSX.Element {
    return (
        <span className="shrink-0 whitespace-nowrap text-occ-foreground-secondary">{children}</span>
    );
}

export const Input = (allProps: InputProps): React.JSX.Element => {
    const {
        ref,
        className,
        inputClassName,
        size,
        leading,
        trailing,
        prepend,
        append,
        disabled: isDisabled = false,
        disabledReason,
        type,
        ...props
    } = allProps;
    const isInvalid = props['aria-invalid'] === true || props['aria-invalid'] === 'true';

    // The chrome is a box around the field rather than the field itself, so a
    // click on the padding or a decoration has to be handed to the input — a
    // flat <input> gave that away for free.
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const attachRef = (node: HTMLInputElement | null): void => {
        innerRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    function focusField(e: React.MouseEvent<HTMLDivElement>): void {
        const target = e.target as HTMLElement;
        if (target.closest("input, button, a, select, textarea, [role='button']")) {
            return;
        }
        e.preventDefault();
        innerRef.current?.focus();
    }

    const field = (
        <div
            data-slot="occ-input"
            data-size={size ?? 'regular'}
            onMouseDown={focusField}
            className={cn(inputVariants({ size, invalid: isInvalid, disabled: isDisabled }),
                className)}
        >
            {hasSlot(leading) && <Decoration>{leading}</Decoration>}
            <span className="flex min-w-0 flex-1 items-center gap-1">
                {hasSlot(prepend) && <Affix>{prepend}</Affix>}
                <input
                    ref={attachRef}
                    type={type}
                    disabled={isDisabled}
                    className={cn(
                        'min-w-0 flex-1 bg-transparent outline-none',
                        'placeholder:text-occ-foreground-tertiary',
                        'selection:bg-occ-brand selection:text-occ-fg-light-1000',
                        inputClassName,
                    )}
                    {...props}
                />
                {hasSlot(append) && <Affix>{append}</Affix>}
            </span>
            {hasSlot(trailing) && <Decoration>{trailing}</Decoration>}
        </div>
    );
    // Written at the call site, the reason keeps the tooltip mounted while it is undefined,
    // so the field is not remounted when it comes and goes.
    if (!Object.hasOwn(allProps, 'disabledReason')) {
        return field;
    }
    return <Hint label={isDisabled ? disabledReason : undefined}>{field}</Hint>;
};

export { inputVariants };
